import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { LEAD_PRIORITIES, LEAD_STATUSES, LEAD_STATUS_BADGE, PRIORITY_BADGE, PROPERTY_TYPES } from "@/lib/constants";
import { formatINR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/PlaceholderPage";

const ALL = "__all__";

const requirementOf = (l) =>
  [l.enquiry_for, l.bhk_requirement ? `${l.bhk_requirement}BHK` : null, l.property_subtype_interested || l.property_type_interested, l.preferred_areas || l.city]
    .filter(Boolean)
    .join(" · ");

const budgetOf = (l) => {
  if (l.budget_min != null && l.budget_max != null) return `${formatINR(l.budget_min)} – ${formatINR(l.budget_max)}`;
  if (l.budget_max != null) return `Up to ${formatINR(l.budget_max)}`;
  return `From ${formatINR(l.budget_min)}`;
};

const isDue = (d) => d && d <= new Date().toISOString().slice(0, 10);

const FilterSelect = ({ value, onChange, placeholder, options, testId, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`rounded-lg bg-white ${className || "w-[160px]"}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return <SelectItem key={v} value={v}>{l}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

export default function LeadsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    type: searchParams.get("property_type") || ALL,
    status: searchParams.get("status") || ALL,
    priority: ALL,
    assigned: ALL,
    due: searchParams.get("due") === "true",
  });
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isAdmin,
  });

  const params = {};
  if (filters.type !== ALL) params.property_type_interested = filters.type;
  if (filters.status !== ALL) params.status = filters.status;
  if (filters.priority !== ALL) params.priority = filters.priority;
  if (isAdmin && filters.assigned !== ALL) params.assigned_to = filters.assigned;
  if (filters.due) params.due_follow_up = true;

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", params],
    queryFn: async () => (await api.get("/leads", { params })).data,
  });

  return (
    <div className="p-6 sm:p-8" data-testid="leads-page">
      <PageHeader
        title={isAdmin ? "Leads" : "My Leads"}
        subtitle={isAdmin ? "Demand side — customer requirements" : "Leads assigned to you"}
      >
        {isAdmin && (
          <Button
            onClick={() => navigate("/leads/new")}
            data-testid="add-lead-button"
            className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Lead
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterSelect value={filters.type} onChange={set("type")} placeholder="All Types" options={PROPERTY_TYPES} testId="lead-filter-type" />
        <FilterSelect value={filters.status} onChange={set("status")} placeholder="All Statuses" options={LEAD_STATUSES} testId="lead-filter-status" className="w-[180px]" />
        <FilterSelect value={filters.priority} onChange={set("priority")} placeholder="All Priorities" options={LEAD_PRIORITIES} testId="lead-filter-priority" className="w-[150px]" />
        {isAdmin && (
          <FilterSelect
            value={filters.assigned}
            onChange={set("assigned")}
            placeholder="All Staff"
            options={[{ value: "unassigned", label: "Unassigned" }, ...users.map((u) => ({ value: u.id, label: u.full_name }))]}
            testId="lead-filter-assigned"
            className="w-[170px]"
          />
        )}
        <Button
          variant={filters.due ? "default" : "outline"}
          onClick={() => set("due")(!filters.due)}
          data-testid="lead-filter-due"
          className={`rounded-lg ${filters.due ? "bg-red-600 hover:bg-red-700 text-white" : "bg-white"}`}
        >
          <CalendarClock className="h-4 w-4 mr-2" /> Due for follow-up
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] overflow-x-auto animate-fade-up">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Requirement</TableHead>
              <TableHead>Budget</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              {isAdmin && <TableHead>Assigned To</TableHead>}
              <TableHead>Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12" data-testid="leads-empty">
                  <p className="text-slate-400">{isAdmin ? "No leads yet for these filters." : "No leads assigned to you yet."}</p>
                  {isAdmin && (
                    <Button onClick={() => navigate("/leads/new")} variant="outline" data-testid="empty-add-lead" className="mt-3 rounded-lg border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                      <Plus className="h-4 w-4 mr-2" /> Add your first lead
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              leads.map((l) => (
                <TableRow key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`lead-row-${l.id}`} className="cursor-pointer hover:bg-emerald-50/40">
                  <TableCell className="font-medium text-emerald-950">{l.customer_name}</TableCell>
                  <TableCell className="text-slate-600">{l.customer_phone_primary}</TableCell>
                  <TableCell className="text-slate-600 max-w-[260px] truncate">{requirementOf(l)}</TableCell>
                  <TableCell className="text-emerald-900 font-medium whitespace-nowrap">{budgetOf(l)}</TableCell>
                  <TableCell><Badge variant="outline" className={PRIORITY_BADGE[l.priority]}>{l.priority}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={LEAD_STATUS_BADGE[l.status]}>{l.status}</Badge></TableCell>
                  {isAdmin && <TableCell className="text-slate-600">{l.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</TableCell>}
                  <TableCell className={isDue(l.next_follow_up_date) ? "text-red-600 font-medium" : "text-slate-600"}>
                    {l.next_follow_up_date || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
